import type { TaskOverviewSortingFields } from '@einwert/valuation-api-client'
import { useTaskOverviewPage } from 'ClientPortal/src/pages/Orders/TaskOverview/TaskOverviewPage.data'
import { TasksTable } from 'ClientPortal/src/pages/Orders/TaskOverview/TasksTable'
import {
    assembleNameWithEmailFallback,
    DisplayUserLink,
    PageGridContainer,
    PageGridItem,
    SearchField,
    TextWithEllipsis,
    TinyButtonWithOptions,
    tokens,
} from 'ComponentLibrary'
import { useState } from 'react'
import { isEmpty, partialRight } from 'lodash'
import { Autocomplete, InputAdornment, ListItem, Stack, TextField } from '@mui/material'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { useGetAllPortfoliosValueOverview } from 'ClientPortal/src/api/Queries/value-overview'
import { ArrowCounterClockwiseIcon, CaretDownIcon, SortAscendingIcon } from '@phosphor-icons/react'
import { useTaskSideDrawer } from 'ComponentLibrary/src/context/TaskSideDrawerContext'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { TaskOverViewPageLoading } from 'ClientPortal/src/pages/Orders/TaskOverview/TaskOverviewPage.load'
import { TASK_STATUS_INVALIDATION_DELAY_IN_MS } from 'ClientPortal/src/api/Queries/tasks'
import { UserBubble } from 'ComponentLibrary/src/molecules/UserBubble'
import { useUrlState } from 'ComponentLibrary/src/hooks/useUrlState'
import { TaskOverviewPageUrlStateSchema } from 'ClientPortal/src/pages/schemas.zod'

import { CaretDown } from 'ComponentLibrary/src/atoms/Select/Select'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'

function translateSortByLabel(sortBy: TaskOverviewSortingFields, translate: (key: string) => string): string {
    switch (sortBy) {
        case '+due_date':
            return translate('sort.dueDateAscending')
        case '-due_date':
            return translate('sort.dueDateDescending')
        case '+title':
            return translate('sort.titleAscending')
        case '-title':
            return translate('sort.titleDescending')
    }
}

export function TasksOverviewPage() {
    const { t } = useTranslation('pages', { keyPrefix: 'taskOverviewPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const userLinkCopy = useUserLinkCopy()
    const [isOrderFilterOpen, setIsOrderFilterOpen] = useState(false)
    const [isPortfolioFilterOpen, setIsPortfolioFilterOpen] = useState(false)

    const { openTaskSideDrawer } = useTaskSideDrawer()

    const { urlState, setUrlState, resetUrlState } = useUrlState(TaskOverviewPageUrlStateSchema, 'client-task-filter')

    const {
        tasks,
        filtersAndSort,
        isTasksPending,
        deleteTask,
        isUserPending,
        isAppraiser,
        users,
        isUsersPending,
        updateTaskStatus,
        user,
        isOrdersInitialLoading,
        orderOptions,
        teamOptions,
        isTeamsLoading,
    } = useTaskOverviewPage({
        isGetOrdersEnabled: isOrderFilterOpen,
        filtersAndSort: urlState,
    })

    const { multiPortfolioValueOverview, isMultiPortfolioValueOverviewPending } = useGetAllPortfoliosValueOverview()
    const portfolioOptions = (multiPortfolioValueOverview?.portfolios || [])
        .filter((portfolio) => portfolio.type !== 'synthetic')
        .map((portfolio) => ({
            id: portfolio.uuid,
            label: portfolio.title,
        }))

    const isTableLoading = isTasksPending || isUserPending

    const isFiltered = Object.values(filtersAndSort).some((filter) => {
        return filter !== undefined && filter !== '' && filter !== null
    })
    const selectedAssignee = users.find((user) => user.id === urlState.assignee?.id)
    const assigneeOptions = !selectedAssignee && urlState.assignee ? [urlState.assignee, ...users] : users

    return (
        <>
            <PageGridContainer withBottomSpace withTopSpace withFooter>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Headline variant="headline1" title={tTerminology('tasks')} />
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                        <SearchField
                            size="small"
                            style={{ flex: 1 }}
                            defaultValue={urlState.searchString}
                            label={t('search')}
                            handleSearch={(searchString) => setUrlState((prev) => ({ ...prev, searchString }))}
                        />
                        <Autocomplete
                            size="small"
                            options={assigneeOptions}
                            groupBy={(option) => option.organization_name}
                            getOptionLabel={(assignee) => {
                                return assembleNameWithEmailFallback(
                                    assignee,
                                    assignee.id === user?.id ? { suffix: t('currentUserSuffix') } : undefined
                                )
                            }}
                            renderInput={(params) => {
                                return (
                                    <TextField
                                        {...params}
                                        label={t('assignee')}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: {
                                                '& .MuiInputBase-root': {
                                                    padding: tokens.SizingSpacing100,
                                                },
                                            },
                                            startAdornment: selectedAssignee && (
                                                <InputAdornment position="start" sx={{ marginRight: 0 }}>
                                                    <UserBubble
                                                        profileImageUrl={selectedAssignee.profile_thumbnail_image_url}
                                                        profileFallbackImageUrl={selectedAssignee.profile_image_url}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )
                            }}
                            value={selectedAssignee ?? urlState.assignee ?? null}
                            onChange={(_, assignee) => setUrlState((prev) => ({ ...prev, assignee }))}
                            sx={{ flex: 1 }}
                            loading={isUsersPending}
                            loadingText={t('loading')}
                            noOptionsText={t('noUsers')}
                            renderOption={(params, option) => {
                                return (
                                    <ListItem {...params} key={option.id} sx={{ gap: tokens.SizingSpacing100 }}>
                                        <DisplayUserLink user={option} size="medium" showProfileOnHover={false} copy={userLinkCopy} />
                                    </ListItem>
                                )
                            }}
                            popupIcon={<CaretDown />}
                        />
                        <Autocomplete
                            open={isOrderFilterOpen}
                            onOpen={() => {
                                setIsOrderFilterOpen(true)
                            }}
                            onClose={() => {
                                setIsOrderFilterOpen(false)
                            }}
                            size="small"
                            value={urlState.order}
                            onChange={(_, order) => setUrlState((prev) => ({ ...prev, order }))}
                            options={orderOptions}
                            renderInput={(params) => <TextField {...params} label={tTerminology('order')} />}
                            sx={{ flex: 1 }}
                            loading={isOrdersInitialLoading}
                            renderOption={(params, option) => (
                                <li {...params} key={option.id}>
                                    {option.label}
                                </li>
                            )}
                            loadingText={t('loading')}
                            noOptionsText={t('noOrders')}
                            popupIcon={<CaretDown />}
                        />
                        <Autocomplete
                            defaultValue={urlState.portfolio}
                            open={isPortfolioFilterOpen}
                            onOpen={() => {
                                setIsPortfolioFilterOpen(true)
                            }}
                            onClose={() => {
                                setIsPortfolioFilterOpen(false)
                            }}
                            size="small"
                            value={urlState.portfolio}
                            onChange={(_, portfolio) => setUrlState((prev) => ({ ...prev, portfolio }))}
                            options={portfolioOptions}
                            renderInput={(params) => <TextField {...params} label={tTerminology('portfolio')} />}
                            sx={{ flex: 1 }}
                            loading={isMultiPortfolioValueOverviewPending}
                            renderOption={(params, option) => (
                                <li {...params} key={option.id}>
                                    {option.label}
                                </li>
                            )}
                            loadingText={t('loading')}
                            noOptionsText={t('noPortfolios')}
                            popupIcon={<CaretDown />}
                        />
                        {!isEmpty(teamOptions) && (
                            <Autocomplete
                                size="small"
                                options={teamOptions}
                                getOptionLabel={(team) => team.label}
                                renderInput={(params) => <TextField {...params} label={t('team')} />}
                                value={urlState.team}
                                onChange={(_, team) => setUrlState((prev) => ({ ...prev, team }))}
                                sx={{ flex: 1 }}
                                loading={isTeamsLoading}
                                loadingText={t('loading')}
                                noOptionsText={t('noTeams')}
                                renderOption={(params, option) => (
                                    <li {...params} key={option.id}>
                                        <TextWithEllipsis tooltipText={option.label}>{option.label}</TextWithEllipsis>
                                    </li>
                                )}
                                popupIcon={<CaretDown />}
                            />
                        )}
                        <IconButton
                            size="small"
                            disabled={!isFiltered}
                            variant="secondary"
                            icon={<ArrowCounterClockwiseIcon weight="bold" size={tokens.SizingIconS} />}
                            onClick={resetUrlState}
                            tooltip={t('resetFilters')}
                        />
                    </Stack>
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Stack direction="row" gap={tokens.SizingSpacing150} justifyContent="space-between">
                        <Checkbox
                            checked={urlState.status === 'open,done'}
                            label={t('showDone')}
                            onChange={(_, checked) => setUrlState((prev) => ({ ...prev, status: checked ? 'open,done' : 'open' }))}
                        />
                        <TinyButtonWithOptions
                            options={[
                                {
                                    title: translateSortByLabel('+due_date', t),
                                    callback: () => setUrlState((prev) => ({ ...prev, sortBy: '+due_date' })),
                                },
                                {
                                    title: translateSortByLabel('-due_date', t),
                                    callback: () => setUrlState((prev) => ({ ...prev, sortBy: '-due_date' })),
                                },
                                {
                                    title: translateSortByLabel('+title', t),
                                    callback: () => setUrlState((prev) => ({ ...prev, sortBy: '+title' })),
                                },
                                {
                                    title: translateSortByLabel('-title', t),
                                    callback: () => setUrlState((prev) => ({ ...prev, sortBy: '-title' })),
                                },
                            ]}
                            label={translateSortByLabel(urlState.sortBy, t)}
                            leftIcon={<SortAscendingIcon color={tokens.ColorIconBoldest} size={tokens.SizingIconS} weight="bold" />}
                            rightIcon={<CaretDownIcon color={tokens.ColorIconSubtle} size={tokens.SizingIconS} weight="bold" />}
                        />
                    </Stack>
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    {!isTableLoading ? (
                        <TasksTable
                            tasks={tasks}
                            deleteTask={deleteTask}
                            canDelete={isAppraiser}
                            onEdit={openTaskSideDrawer}
                            onSelectTaskStatus={partialRight(updateTaskStatus, TASK_STATUS_INVALIDATION_DELAY_IN_MS)}
                            isFiltered={isFiltered}
                            portfolioOptions={portfolioOptions}
                            teamOptions={teamOptions}
                        />
                    ) : (
                        <TaskOverViewPageLoading />
                    )}
                </PageGridItem>
            </PageGridContainer>
        </>
    )
}
